import React from 'react'
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer'
import { getFarmLogoUrl } from '../../supabase-client'
import { formatInvoiceDataDisplayName } from '../../utils/invoiceUtils.js'
import { parseDateString } from '../../utils/dateUtils'

// Define styles for the PDF
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Times-Roman',
    fontSize: 10,
    paddingTop: 35,
    paddingLeft: 35,
    paddingRight: 35,
    paddingBottom: 65,
    lineHeight: 1.5,
  },
  
  // Header Section
  header: {
    flexDirection: 'row',
    marginBottom: 20,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
  },
  
  headerLeft: {
    flex: 1,
  },
  
  headerRight: {
    flex: 1,
    alignItems: 'flex-end',
  },
  
  logo: {
    maxWidth: 100,
    maxHeight: 80,
    marginBottom: 10,
    objectFit: 'contain',
  },
  
  companyName: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  
  companyInfo: {
    fontSize: 9,
    lineHeight: 1.3,
    marginBottom: 2,
  },
  
  invoiceTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 10,
    textAlign: 'right',
  },
  
  invoiceDetails: {
    fontSize: 10,
    textAlign: 'right',
    lineHeight: 1.4,
  },
  
  // Customer Section
  customerSection: {
    flexDirection: 'row',
    marginBottom: 20,
  },
  
  customerInfo: {
    flex: 1,
  },
  
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
    textDecoration: 'underline',
  },
  
  customerText: {
    fontSize: 10,
    lineHeight: 1.4,
    marginBottom: 2,
  },
  
  // Mini Statement Section
  miniStatementSection: {
    marginBottom: 20,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#000000',
  },
  
  miniStatementTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    backgroundColor: '#f0f0f0',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
  },
  
  miniStatementTable: {
    padding: 10,
  },
  
  miniStatementRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 3,
    fontSize: 9,
  },
  
  // Branch Totals Section
  branchTotalsSection: {
    marginBottom: 20,
  },
  
  totalsTable: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#000000',
  },
  
  totalsHeader: {
    flexDirection: 'row',
    backgroundColor: '#f0f0f0',
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    padding: 8,
  },
  
  totalsHeaderText: {
    flex: 1,
    fontSize: 11,
    fontWeight: 'bold',
  },
  
  totalsRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#cccccc',
    padding: 8,
  },
  
  totalsCell: {
    flex: 1,
    fontSize: 10,
  },
  
  totalAmountCell: {
    flex: 1,
    fontSize: 10,
    textAlign: 'right',
    fontWeight: 'bold',
  },
  
  grandTotalRow: {
    flexDirection: 'row',
    backgroundColor: '#f0f0f0',
    padding: 8,
    borderTopWidth: 2,
    borderTopColor: '#000000',
  },
  
  grandTotalLabel: {
    flex: 1,
    fontSize: 12,
    fontWeight: 'bold',
  },
  
  grandTotalAmount: {
    flex: 1,
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'right',
  },
  
  // Items Table Section
  itemsSection: {
    marginBottom: 20,
  },
  
  branchTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 10,
    marginTop: 15,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
  },
  
  itemsTable: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#000000',
    marginBottom: 15,
  },
  
  itemsHeader: {
    flexDirection: 'row',
    backgroundColor: '#f0f0f0',
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    padding: 6,
  },
  
  itemsHeaderText: {
    fontSize: 9,
    fontWeight: 'bold',
  },
  
  itemsRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#cccccc',
    padding: 6,
    minHeight: 20,
  },
  
  itemsCell: {
    fontSize: 9,
    paddingRight: 5,
  },
  
  // Column widths for items table
  dateCol: { width: '12%' },
  consignmentCol: { width: '15%' },
  descriptionCol: { width: '35%' },
  qtyCol: { width: '12%', textAlign: 'right' },
  priceCol: { width: '13%', textAlign: 'right' },
  amountCol: { width: '13%', textAlign: 'right' },
  
  branchSubtotal: {
    flexDirection: 'row',
    backgroundColor: '#f8f8f8',
    padding: 6,
    borderTopWidth: 1,
    borderTopColor: '#000000',
  },
  
  subtotalLabel: {
    flex: 1,
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'right',
    paddingRight: 50,
  },
  
  subtotalAmount: {
    width: '13%',
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'right',
  },
  
  // Footer Section
  footer: {
    marginTop: 40,
    marginLeft: 35,
    marginRight: 35,
    textAlign: 'center',
    borderTopWidth: 1,
    borderTopColor: '#000000',
    paddingTop: 10,
  },
  
  paymentTerms: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 5,
    textAlign: 'center',
  },
  
  pageNumber: {
    fontSize: 9,
    color: '#666666',
  },
})

const InvoiceTemplate = ({ invoiceData, farmInfo, miniStatement, showMiniStatement, invoiceNumberRange = null }) => {
  // Get logo URL from file path
  const logoUrl = farmInfo?.logo_file_path ? getFarmLogoUrl(farmInfo.logo_file_path) : null

  // Debug farm info
  console.log('InvoiceTemplate received farmInfo:', {
    business_name: farmInfo?.business_name,
    logo_file_path: farmInfo?.logo_file_path,
    logo_url: logoUrl,
    has_logo: !!logoUrl
  })

  // Calculate totals by branch
  const branchTotals = invoiceData.branches.map(branch => {
    // Handle 'main' branch (sales with null branch_id) and actual branches
    const branchSales = invoiceData.sales.filter(sale =>
      (branch.id === 'main' && !sale.branch_id) || (sale.branch_id === branch.id)
    )

    const total = branchSales.reduce((sum, sale) => {
      return sum + sale.sale_items.reduce((itemSum, item) =>
        itemSum + parseFloat(item.sub_total), 0
      )
    }, 0)
    return { branch, total }
  })

  const grandTotal = branchTotals.reduce((sum, bt) => sum + bt.total, 0)

  // Format currency
  const formatCurrency = (amount) => `£${amount.toFixed(2)}`

  // Format date
  const formatDate = (dateString) => {
    const date = parseDateString(dateString)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  // Generate payment terms text
  const paymentTerms = invoiceData.customer.payment_terms || '30'
  const paymentTermsText = `Payment due within ${paymentTerms} days`

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header Section */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {logoUrl && (
              <View style={{ marginBottom: 10 }}>
                <Image 
                  style={styles.logo} 
                  src={logoUrl}
                />
              </View>
            )}
            <Text style={styles.companyName}>{farmInfo.business_name}</Text>
            {farmInfo.address_line_1 && (
              <Text style={styles.companyInfo}>{farmInfo.address_line_1}</Text>
            )}
            {farmInfo.address_line_2 && (
              <Text style={styles.companyInfo}>{farmInfo.address_line_2}</Text>
            )}
            {farmInfo.city && (
              <Text style={styles.companyInfo}>
                {farmInfo.city}{farmInfo.postcode && `, ${farmInfo.postcode}`}
              </Text>
            )}
            {farmInfo.country && farmInfo.country !== 'United Kingdom' && (
              <Text style={styles.companyInfo}>{farmInfo.country}</Text>
            )}
            {farmInfo.phone && (
              <Text style={styles.companyInfo}>Tel: {farmInfo.phone}</Text>
            )}
            {farmInfo.email && (
              <Text style={styles.companyInfo}>Email: {farmInfo.email}</Text>
            )}
            {farmInfo.company_number && (
              <Text style={styles.companyInfo}>Company No: {farmInfo.company_number}</Text>
            )}
          </View>
          
          <View style={styles.headerRight}>
            <Text style={styles.invoiceTitle}>
              {invoiceNumberRange ? 'INVOICES' : 'INVOICE'}
            </Text>
            <Text style={styles.invoiceDetails}>
              {invoiceNumberRange || formatInvoiceDataDisplayName(invoiceData)}
            </Text>
            <Text style={styles.invoiceDetails}>
              Date: {formatDate(invoiceData.date)}
            </Text>
            {farmInfo.bank_name && (
              <>
                <Text style={[styles.invoiceDetails, { marginTop: 10 }]}>
                  Bank: {farmInfo.bank_name}
                </Text>
                {farmInfo.bank_account_number && (
                  <Text style={styles.invoiceDetails}>
                    Account: {farmInfo.bank_account_number}
                  </Text>
                )}
                {farmInfo.bank_sort_code && (
                  <Text style={styles.invoiceDetails}>
                    Sort Code: {farmInfo.bank_sort_code}
                  </Text>
                )}
              </>
            )}
          </View>
        </View>

        {/* Customer Section */}
        <View style={styles.customerSection}>
          <View style={styles.customerInfo}>
            <Text style={styles.sectionTitle}>Bill To:</Text>
            <Text style={styles.customerText}>{invoiceData.customer.name}</Text>
            {invoiceData.customer.email && (
              <Text style={styles.customerText}>{invoiceData.customer.email}</Text>
            )}
            {invoiceData.customer.phone && (
              <Text style={styles.customerText}>{invoiceData.customer.phone}</Text>
            )}
          </View>
        </View>

        {/* Mini Statement Section */}
        {showMiniStatement && miniStatement && miniStatement.length > 0 && (
          <View style={styles.miniStatementSection}>
            <Text style={styles.miniStatementTitle}>Account Summary</Text>
            <View style={styles.miniStatementTable}>
              {miniStatement.map((entry, index) => (
                <View key={index} style={styles.miniStatementRow}>
                  <Text>{formatDate(entry.date)}</Text>
                  <Text>{entry.description}</Text>
                  <Text>{formatCurrency(entry.amount)}</Text>
                </View>
              ))}
              <View style={[styles.miniStatementRow, { borderTopWidth: 1, borderTopColor: '#000000', paddingTop: 5, marginTop: 5 }]}>
                <Text style={{ fontWeight: 'bold' }}>Previous Balance:</Text>
                <Text></Text>
                <Text style={{ fontWeight: 'bold' }}>{formatCurrency(invoiceData.balance)}</Text>
              </View>
            </View>
          </View>
        )}

        {/* Items by Branch Section */}
        <View style={styles.itemsSection}>
          {branchTotals.map(({ branch }, branchIndex) => {
            // Handle 'main' branch (sales with null branch_id) and actual branches
            const branchSales = invoiceData.sales.filter(sale =>
              (branch.id === 'main' && !sale.branch_id) || (sale.branch_id === branch.id)
            )
            if (branchSales.length === 0) return null

            const branchTotal = branchSales.reduce((sum, sale) => {
              return sum + sale.sale_items.reduce((itemSum, item) =>
                itemSum + parseFloat(item.sub_total), 0
              )
            }, 0)

            return (
              <View key={branchIndex}>
                {/* Only show branch title if it's not Main Company or if there are multiple branches */}
                {branchTotals.length > 1 && branch.id !== 'main' && (
                  <Text style={styles.branchTitle}>
                    {branch.name}{branch.address ? ` - ${branch.address}` : ''}
                  </Text>
                )}

                <View style={styles.itemsTable}>
                  <View style={styles.itemsHeader}>
                    <Text style={[styles.itemsHeaderText, styles.dateCol]}>Date</Text>
                    <Text style={[styles.itemsHeaderText, styles.consignmentCol]}>Tray</Text>
                    <Text style={[styles.itemsHeaderText, styles.descriptionCol]}>Description</Text>
                    <Text style={[styles.itemsHeaderText, styles.qtyCol]}>Qty</Text>
                    <Text style={[styles.itemsHeaderText, styles.priceCol]}>Price</Text>
                    <Text style={[styles.itemsHeaderText, styles.amountCol]}>Amount</Text>
                  </View>

                  {branchSales.map((sale) => {
                    // Sort sale items by product type: A+, A, Sample, Gift
                    const sortedItems = [...sale.sale_items].sort((a, b) => {
                      const typeOrder = { 'A+ Class': 1, 'A Class': 2, 'Sample': 3, 'Gift': 4 }
                      const typeA = a.product_type?.name || ''
                      const typeB = b.product_type?.name || ''
                      const orderA = typeOrder[typeA] || 99
                      const orderB = typeOrder[typeB] || 99

                      if (orderA !== orderB) return orderA - orderB

                      // Within same type, sort by strain name alphabetically
                      const strainA = a.product?.strain || ''
                      const strainB = b.product?.strain || ''
                      return strainA.localeCompare(strainB)
                    })

                    return sortedItems.map((item, itemIndex) => {
                      // Description + quantity vary by line-item type:
                      // block (block_format_id), growing material (growing_material_id), or fresh mushroom.
                      let description
                      let qtyText
                      if (item.block_format_id && item.block_format) {
                        description = `${item.product?.strain || 'Unknown'} Fruiting Block (${item.block_format.name})`
                        qtyText = `${parseFloat(item.quantity).toFixed(0)} unit${parseFloat(item.quantity) !== 1 ? 's' : ''}`
                      } else if (item.growing_material_id && item.growing_material) {
                        const label = item.growing_material.unit_label || 'unit'
                        description = item.growing_material.name || 'Growing Material'
                        qtyText = `${parseFloat(item.quantity).toFixed(0)} ${label}${parseFloat(item.quantity) !== 1 ? 's' : ''}`
                      } else {
                        description = item.product
                          ? `${item.product.strain || 'Unknown'} (${item.product_type?.name || 'Type'})`
                          : 'Product Information Missing'
                        qtyText = `${parseFloat(item.quantity).toFixed(1)} kg`
                      }

                      return (
                      <View key={`${sale.id}-${itemIndex}`} style={styles.itemsRow}>
                        <Text style={[styles.itemsCell, styles.dateCol]}>
                          {formatDate(sale.date)}
                        </Text>
                        <Text style={[styles.itemsCell, styles.consignmentCol]}>
                          {sale.sales_id || '—'}
                        </Text>
                        <Text style={[styles.itemsCell, styles.descriptionCol]}>
                          {description}
                          {item.price_tier && item.price_tier.name !== 'retail' && (
                            ` [${item.price_tier.display_name}]`
                          )}
                          {item.discount_percent > 0 && (
                            ` - ${item.discount_percent}% discount`
                          )}
                          {item.discount_note && ` (${item.discount_note})`}
                        </Text>
                        <Text style={[styles.itemsCell, styles.qtyCol]}>
                          {qtyText}
                        </Text>
                        <Text style={[styles.itemsCell, styles.priceCol]}>
                          {formatCurrency(parseFloat(item.price))}
                        </Text>
                        <Text style={[styles.itemsCell, styles.amountCol]}>
                          {formatCurrency(parseFloat(item.sub_total))}
                        </Text>
                      </View>
                      )
                    })
                  })}

                  <View style={styles.branchSubtotal}>
                    <Text style={styles.subtotalLabel}>Total:</Text>
                    <Text style={styles.subtotalAmount}>{formatCurrency(branchTotal)}</Text>
                  </View>
                </View>
              </View>
            )
          })}
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.paymentTerms}>{paymentTermsText}</Text>
          <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => 
            `Page ${pageNumber} of ${totalPages}`
          } />
        </View>
      </Page>
    </Document>
  )
}

export default InvoiceTemplate
